import { useState, useEffect } from 'react';
import { Player, Broadcast } from '@livepeer/react';
import axios from 'axios';
import Chat from './Chat';
import './ActiveStreams.css';

const API_URL = process.env.NODE_ENV === 'development' 
  ? process.env.REACT_APP_API_URL || 'http://localhost:3001'
  : window.location.origin;

const BroadcastComponent = () => {
  const [streamName, setStreamName] = useState('');
  const [stream, setStream] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [username] = useState('Streamer');
  const [activeStreams, setActiveStreams] = useState([]);
  const [isLoadingStreams, setIsLoadingStreams] = useState(true);

  // Fetch active streams
  useEffect(() => {
    const fetchActiveStreams = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/streams`);
        const streams = response.data.filter(stream => stream.isActive);
        setActiveStreams(streams);
      } catch (err) {
        console.error('Error fetching streams:', err);
      } finally {
        setIsLoadingStreams(false);
      }
    };

    fetchActiveStreams();
    const interval = setInterval(fetchActiveStreams, 30000);
    return () => clearInterval(interval);
  }, []);

  const createStream = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/api/stream`, {
        name: streamName
      });
      
      console.log('Stream response:', response.data);
      
      if (!response.data?.streamKey) {
        throw new Error('Stream key not found in response');
      }
      
      setStream(response.data);
    } catch (err) {
      console.error('Stream creation error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to create stream');
    } finally {
      setIsLoading(false);
    }
  };

  const getViewerUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/watch?v=${stream.playbackId}`;
  };

  const copyViewerUrl = () => {
    navigator.clipboard.writeText(getViewerUrl());
  };

  return (
    <div className="broadcast-page">
      {!stream ? (
        <>
          <div className="create-stream-section">
            <h1>Start Your Stream</h1>
            <p className="subtitle">Share your moments live with other yappers</p>
            
            <form onSubmit={createStream} className="create-stream-form">
              <input
                type="text"
                value={streamName}
                onChange={(e) => setStreamName(e.target.value)}
                placeholder="Give your stream a catchy name"
                required
              />
              <button type="submit" disabled={isLoading}>
                {isLoading ? 'Creating...' : 'Start Streaming'}
              </button>
              {error && <div className="error">{error}</div>}
            </form>
          </div>

          <div className="divider">
            <span>OR</span>
          </div>

          <div className="active-streams-section">
            <h2>Watch and Listen to Other Yappers</h2>
            {isLoadingStreams ? (
              <div className="loading">Loading active streams...</div>
            ) : activeStreams.length > 0 ? (
              <div className="streams-grid">
                {activeStreams.map(activeStream => (
                  <div key={activeStream.id} className="stream-card">
                    <div className="stream-preview">
                      <Player
                        title={activeStream.name}
                        playbackId={activeStream.playbackId}
                        autoPlay
                        muted
                      />
                    </div>
                    <div className="stream-info">
                      <h4>{activeStream.name}</h4>
                      <div className="stream-stats">
                        <p>
                          <span className="stat-label">Ingest Rate:</span>
                          <span className="stat-value">{(activeStream.ingestRate / 1024 / 1024).toFixed(2)} Mbps</span>
                        </p>
                        <p>
                          <span className="stat-label">Outgoing Rate:</span>
                          <span className="stat-value">{(activeStream.outgoingRate / 1024 / 1024).toFixed(2)} Mbps</span>
                        </p>
                        <p>
                          <span className="stat-label">Last Seen:</span>
                          <span className="stat-value">{new Date(activeStream.lastSeen).toLocaleTimeString()}</span>
                        </p>
                      </div>
                      <a 
                        href={`/watch?v=${activeStream.playbackId}`}
                        className="watch-button"
                      >
                        Watch Stream
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-streams">
                No active streams at the moment. Be the first one to start streaming!
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="stream-info">
          <h2>{stream.name}</h2>
          
          <div className="broadcast-player">
            <Broadcast
              streamKey={stream.streamKey}
              controls
              aspectRatio={16/9}
              muted
              onError={(error) => {
                console.error('Broadcast error:', error);
                setError('Failed to start webcam stream. Please check camera permissions.');
              }}
            />
          </div>

          <div className="stream-details">
            <div className="viewer-link">
              <p><strong>Share your stream:</strong></p>
              <div className="link-container">
                <input
                  type="text"
                  value={getViewerUrl()}
                  readOnly
                  className="viewer-url"
                />
                <button onClick={copyViewerUrl} className="copy-btn">
                  Copy Link
                </button>
              </div>
              <p className="help-text">Share this link with your viewers</p>
            </div>

            <div className="stream-instructions">
              <h3>Want to stream from OBS?</h3>
              <p>You can also use these settings in your streaming software:</p>
              <ul>
                <li>
                  <strong>Server URL:</strong> 
                  <span className="monospace">rtmp://rtmp.livepeer.com/live</span>
                </li>
                <li>
                  <strong>Stream Key:</strong> 
                  <span className="monospace">{stream.streamKey}</span>
                </li>
              </ul>
            </div>
          </div>
          
          {error && <div className="error">{error}</div>}
        </div>
      )}
      
      {stream && (
        <div className="viewer-chat">
          <Chat streamId={stream.playbackId} username={username} />
        </div>
      )}
    </div>
  );
};

export default BroadcastComponent;